import { Link } from 'react-router-dom';

import {
  DataTable,
  DataTableHead,
  DataTableRow,
  Heading,
  Link as HCRLink,
  Tag,
  Spinner,
  Infobox,
  Button,
} from '@flixbus/honeycomb-react';
import { DateTime } from 'luxon';
import {
  Icon,
  IconCalendar,
  IconDot,
  IconCheckmarkStrong,
  IconDownload,
  IconEye,
} from '@flixbus/honeycomb-icons-react';
import { BatchStatus } from '../../types/Voucher';
import { Pagination } from '../../components/Pagination';
import { useVoucherBatchesList } from '../../context/VoucherBatchesListProvider';

import styles from './dashboard.module.scss';

const statusLabels: Record<BatchStatus, string> = {
  waiting: 'Waiting',
  started: 'Started',
  finished: 'Finished',
  error: 'Error',
  canceled: 'Canceled',
};

const DISABLED_STATUSES: BatchStatus[] = ['error', 'canceled'];
const IN_PROGRESS_STATUSES: BatchStatus[] = ['waiting', 'started'];

const BatchList = () => {
  const { batches, page, totalPages, loading, error, setPage, refetch } = useVoucherBatchesList();

  return (
    <>
      <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '6px' }}>
        <div className={styles.listheading}>
          <Heading size={2}>Batch list</Heading>
        </div>
        {loading ? (
          <Spinner extraClasses={styles.spinner} size='xl' />
        ) : error ? (
          <Infobox appearance='danger' aria-label='Error loading batches'>
            <p>Something went wrong while loading your batches. Please try again.</p>
            <Button size='sm' onClick={() => refetch()}>
              Retry
            </Button>
          </Infobox>
        ) : batches.length === 0 ? (
          <span>No batches found.</span>
        ) : (
          <DataTable
            extraClasses={styles.table}
            plain={true}
            small={false}
            collapsed={false}
            scrollable={true}
            headers={
              <DataTableHead extraClasses={styles.head}>
                <th className={styles.header} scope='col'>
                  Prefix
                </th>
                <th className={styles.header} scope='col'>
                  Status
                </th>
                <th className={styles.header} scope='col'>
                  Batch size
                </th>
                <th className={styles.header} scope='col'>
                  Remaining
                </th>
                <th className={styles.header} scope='col'>
                  Specification
                </th>
                <th className={styles.header} scope='col'>
                  Created by
                </th>
                <th className={styles.header} scope='col'>
                  Created at
                </th>
                <th className={styles.header} scope='col'>
                  Download
                </th>
              </DataTableHead>
            }
          >
            {batches.map(batch => {
              const parsedCreatedAt = DateTime.fromISO(batch.createdAt).setLocale('de');
              const createdAtFormatted = parsedCreatedAt.isValid
                ? parsedCreatedAt.toFormat('dd LLL yyyy')
                : 'N/A';
              const isFinished = batch.status === 'finished';
              const isInProgress = IN_PROGRESS_STATUSES.includes(batch.status);

              return (
                <DataTableRow key={batch.id}>
                  <td className={styles.cell}>{batch.details.prefix}</td>
                  <td className={styles.cell}>
                    <Tag
                      {...(isFinished
                        ? {
                            tagColor: 'var(--hcr-success-dark-color)',
                            tagTextColor: 'var(--hcr-success-dark-color)',
                          }
                        : isInProgress
                          ? {
                              tagColor: 'var(--hcr-neutral-color)',
                              tagTextColor: 'var(--hcr-neutral-color)',
                            }
                          : {})}
                      disabled={DISABLED_STATUSES.includes(batch.status)}
                      display='outlined'
                    >
                      {isInProgress && <Icon InlineIcon={IconDot} />}
                      {isFinished && <Icon InlineIcon={IconCheckmarkStrong} />}
                      {statusLabels[batch.status] ?? batch.status}
                    </Tag>
                  </td>
                  <td className={styles.cell}>{batch.batchSize}</td>
                  <td className={styles.cell}>{batch.remainingSize}</td>
                  <td className={styles.cell}>{batch.specification || 'N/A'}</td>
                  <td className={styles.cell}>{batch.details.author || 'N/A'}</td>
                  <td className={styles.cell}>
                    <Tag extraClasses={styles.tag} display='outlined'>
                      <Icon appearance='primary' InlineIcon={IconCalendar} />
                      {createdAtFormatted}
                    </Tag>
                  </td>
                  <td className={styles.cell}>
                    <HCRLink RouterLink={Link} to={`/batch/${batch.id}`}>
                      <Icon InlineIcon={isFinished ? IconDownload : IconEye} />
                      {isFinished ? 'Download' : 'View'}
                    </HCRLink>
                  </td>
                </DataTableRow>
              );
            })}
          </DataTable>
        )}
      </div>
      <div className={styles.pagination}>
        <Pagination
          totalPages={totalPages}
          currentPage={page}
          onPageChange={setPage}
          loading={loading}
        />
      </div>
    </>
  );
};

export default BatchList;
